import React, { useEffect, useContext, useState } from 'react';
import FormDataContext from 'integrations/contexts/FormDataContext';

import { qualifyUrl } from 'util/URLUtils';
import fetch from 'logic/rest/FetchProvider';

import { ApiRoutes } from './common/Routes';
import formValidation from './utils/formValidation';
import type { ErrorMessageType, HandleFieldUpdateType, HandleSubmitType } from './types';

import AWSAuthenticationTypes, { AWS_AUTH_TYPES } from '../common/aws/AWSAuthenticationTypes';
import { renderOptions } from '../common/Options';
import FormWrap from '../common/FormWrap';
import ValidatedInput from '../common/ValidatedInput';

type StepAuthorizeProps = {
  onSubmit: HandleSubmitType;
  onChange: HandleFieldUpdateType;
};

const StepAuthorize = ({ onSubmit, onChange }: StepAuthorizeProps) => {
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    fetch('GET', qualifyUrl(ApiRoutes.INTEGRATIONS.AWSSecurityLake.GET_AWS_REGIONS)).then((response) => {
      const region = Object.keys(response).map((key) => ({ label: response[key], value: key }));
      setRegions(region);
    });
  }, []);

  const { formData } = useContext(FormDataContext);
  const [formError, setFormError] = useState<ErrorMessageType>(null);

  const { awsSecret } = formData;
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    setLoading(true);

    fetch('POST', qualifyUrl(ApiRoutes.INTEGRATIONS.AWSSecurityLake.CHECK_CREDENTIALS), {
      aws_access_key: formData?.awsAccessKey?.value || '',
      aws_secret_key: formData?.awsSecretKey?.value || '',
      security_lake_queue_name: formData?.securityLakeSqsQueueName?.value || '',
      aws_region: formData?.securityLakeRegion?.value || '',
      assume_role_arn: formData?.assumeRoleArn?.value || '',
    })
      .then((result: any) => {
        if (result.result === 'valid') {
          setFormError(null);
          onSubmit();
        } else {
          setFormError({
            full_message: result.result,
            nice_message: 'Unable to connect to AWS Security Lake using provided configuration.',
          });
        }

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);

        setFormError({
          full_message: err.message,
          nice_message: 'Unable to connect to AWS Security Lake using provided configuration.',
        });
      });
  };

  const authType = formData.awsAuthenticationType && formData.awsAuthenticationType.value;

  const isFormValid = formValidation.isFormValid(
    [
      'awsSecurityLakeName',
      ...(authType !== AWS_AUTH_TYPES.automatic ? ['awsAccessKey', 'awsSecretKey'] : []),
      'securityLakeRegion',
    ],
    formData,
  );

  return (
    <FormWrap
      onSubmit={handleSubmit}
      buttonContent="Verify Connection &amp; Proceed"
      disabled={isFormValid}
      loading={loading}
      title=""
      error={formError}
      description="">
      <ValidatedInput
        className=""
        id="awsSecurityLakeName"
        type="text"
        fieldData={formData.awsSecurityLakeName}
        onChange={onChange}
        placeholder="Input Name"
        label="Input Name"
        autoComplete="off"
        help="Select a name of your new input that describes it."
        defaultValue={awsSecret?.value}
        required
      />
      <AWSAuthenticationTypes onChange={onChange} />
      <ValidatedInput
        type="select"
        id="securityLakeRegion"
        onChange={onChange}
        fieldData={formData.securityLakeRegion}
        help="Security Lake region where the subscriber is created."
        required
        label="Security Lake Region">
        {renderOptions(regions, 'Choose your account region', false)}
      </ValidatedInput>

      <ValidatedInput
        className=""
        id="securityLakeSqsQueueName"
        type="text"
        onChange={onChange}
        fieldData={formData.securityLakeSqsQueueName}
        help="SQS queue name created by the Security Lake Subscriber"
        label="SQS Queue Name"
        required
      />
      <ValidatedInput
        type="text"
        id="assumeRoleArn"
        fieldData={formData.assumeRoleArn}
        label="AWS Assume Role (ARN)"
        onChange={onChange}
        help="Amazon Resource Name with required cross account permission"
        placeholder="arn:aws:sts::123456789012:assumed-role/some-role"
        maxLength={2048}
      />
    </FormWrap>
  );
};

export default StepAuthorize;
